'use strict';

import _ from 'lodash';

import React, { Component } from 'react';
import availablePlanTypes from 'ck-core/source/functions/availablePlanTypes';
import purchaseStateConstants from 'ck-core/source/schemas/contribs/purchaseStateConstants';
import {
	ACTIVE_PLAN,
	NO_ACTIVE_PLAN,
	ACTIVE_SUBSCRIPTION,
	FAILED_SUBSCRIPTION,
	PAST_DUE_SUBSCRIPTION,
	CANCELLED_SUBSCRIPTION
} from './CurrentPaymentPlanStatus';
import { lang } from './CurrentPlanStatusAndActionsLang';
import Product from '../../data/Product';
import products from 'ck-core/source/data/products';
import CurrentPaymentPlanLang from './CurrentPaymentPlanLang';
import CurrenPlanInfoPropType from './CurrentPlanInfoPropType';
import PropTypes from 'prop-types';
import ReactCKImage from 'ck-core-client/source/ui/ReactCKImage';

export default class CurrentPlanStatusAndActions extends Component {
	CancelSubscriptionButton() {
		const id = 'cancelSubscription';
		return (
			<button key={id} id={id} onClick={this.props.goToCancelPage}>
				{CurrentPaymentPlanLang.BUTTONS.CANCEL_SUBSCRIPTION}
			</button>
		);
	}

	ReactivateSubscriptionButton(subscriptionId) {
		const id = 'reactivateSubscription';
		return (
			<button
				key={id}
				id={id}
				onClick={() => {
					CK.clientSockets.users.emit('reactivateSubscription', { subscriptionId });
				}}
			>
				{CurrentPaymentPlanLang.BUTTONS.REACTIVATE_SUBSCRIPTION}
			</button>
		);
	}

	UpdatePaymentDetailsButton() {
		const id = 'updatePaymentDetails';
		return (
			<button
				key={id}
				id={id}
				onClick={() => {
					this.props.showPaymentForm({
						isUpdateDetails: true,
						wasPastDue:
							_.get(this.props.planInfo, 'state') === purchaseStateConstants.SUBSCRIPTION_PAST_DUE
					});
				}}
			>
				{CurrentPaymentPlanLang.BUTTONS.UPDATE_DETAILS}
			</button>
		);
	}

	renderChangePlanButton() {
		const id = 'changePlan';
		return (
			<button key={id} id={id} onClick={this.props.goToChangePlanPage}>
				{CurrentPaymentPlanLang.BUTTONS.CHANGE_PLAN}
			</button>
		);
	}

	renderProducts(products) {
		return _.map(products, product => (
			<div key={product.id} className="CurrentPaymentPlanProduct">
				<ReactCKImage name={product.icon} />
				{product.name}
			</div>
		));
	}

	formatDate(date) {
		return moment(date).format('Do MMMM YYYY');
	}

	getProductsOwned(planInfo) {
		const productsOwned = [];

		if (_.get(planInfo, 'currentPlan')) {
			const productName = planInfo.currentPlan.product();

			if (productName === products.BUNDLE) {
				// The user owns a bundle, so they can't upgrade.
				productsOwned.push(Product.MINECRAFT);
				productsOwned.push(Product.ROBLOX);
			} else if (productName === products.MINECRAFT) {
				// The user's active product is Minecraft, so they can upgrade
				productsOwned.push(Product.MINECRAFT);
			} else if (productName === products.ROBLOX) {
				// The user's active product is Roblox, so they haven't upgraded yet.
				productsOwned.push(Product.ROBLOX);
			}
		}

		return productsOwned;
	}

	getPlanStatusAndActions(planInfo) {
		const plan = {
			status: null,
			products: [],
			actions: []
		};

		switch (_.get(planInfo, 'state')) {
			case undefined:
			case purchaseStateConstants.NO_PLAN:
			case purchaseStateConstants.TRIAL_CANCELLED:
			case purchaseStateConstants.CAMP_ADMIN:
			case purchaseStateConstants.CAMP_LEADER:
			case purchaseStateConstants.CAMP_STUDENT:
			case purchaseStateConstants.CAMP_STUDENT_NO_LICENCED_GROUP:
			case purchaseStateConstants.EXPIRED:
			case purchaseStateConstants.REVOKED:
				plan.status = NO_ACTIVE_PLAN;
				plan.actions = [this.renderChangePlanButton()];

				break;

			case purchaseStateConstants.TRIAL_ONE_OFF:
			case purchaseStateConstants.ONE_OFF:
			case purchaseStateConstants.ONE_OFF_PENDING_SUBSCRIPTION: {
				plan.status = ACTIVE_PLAN;

				if (!planInfo.plan.lifetime()) {
					plan.expirationDate = this.formatDate(planInfo.endDate);
				}

				const available = availablePlanTypes(CK.user.currentPlanType());

				if (available.length) {
					plan.actions = [this.renderChangePlanButton()];
				}

				break;
			}
			case purchaseStateConstants.TRIAL:
			case purchaseStateConstants.SUBSCRIPTION:
			case purchaseStateConstants.SUBSCRIPTION_PENDING_ONE_OFF:
			case purchaseStateConstants.SUBSCRIPTION_PAST_DUE: {
				switch (planInfo.state) {
					case purchaseStateConstants.SUBSCRIPTION_PAST_DUE:
						plan.status = PAST_DUE_SUBSCRIPTION;
						break;

					case purchaseStateConstants.SUBSCRIPTION_PENDING_ONE_OFF:
						plan.status = ACTIVE_PLAN;
						break;

					default:
						plan.status = ACTIVE_SUBSCRIPTION;
						break;
				}

				plan.actions = [
					this.renderChangePlanButton(),
					planInfo.state !== purchaseStateConstants.SUBSCRIPTION_PENDING_ONE_OFF &&
						this.CancelSubscriptionButton(),
					planInfo.state !== purchaseStateConstants.SUBSCRIPTION_PENDING_ONE_OFF &&
						this.UpdatePaymentDetailsButton()
				];
				plan.renewalDate = this.formatDate(planInfo.endDate);

				if (planInfo.nextEndDate) {
					plan.nextEndDate = planInfo.hasLifetimePlan
						? null
						: this.formatDate(planInfo.nextEndDate);
				}

				if (planInfo.nextPayment) {
					plan.price = CK.fn.formatPrice(
						planInfo.nextPayment.currency,
						planInfo.nextPayment.price,
						{
							spaceCurrencySymbol: false,
							html: false
						}
					);
				}

				break;
			}

			case purchaseStateConstants.SUBSCRIPTION_CANCELLED:
				plan.status = CANCELLED_SUBSCRIPTION;
				plan.expirationDate = this.formatDate(planInfo.endDate);
				plan.actions = [
					this.ReactivateSubscriptionButton(planInfo.subscription.id()),
					this.renderChangePlanButton()
				];

				break;

			// TODO CORE-597 POST-PROC: nextCoupon - do we display info about upcoming coupons?
		}

		const productsOwned = this.getProductsOwned(planInfo, plan.status);

		if (plan.status !== NO_ACTIVE_PLAN) {
			plan.products = productsOwned;
		}

		plan.isLifetime = planInfo.hasLifetimePlan;

		const statusInformation = this.getPlanStatusInformation(plan);

		return {
			statusInformation,
			actions: plan.actions
		};
	}

	getPlanStatusInformation(plan) {
		switch (plan.status) {
			case ACTIVE_PLAN:
				return (
					<div>
						<div>{lang.ACTIVE_PLAN.MAIN}</div>
						<div>{this.renderProducts(plan.products)}</div>
						{(plan.isLifetime && <div>{lang.ACTIVE_PLAN.LIFETIME_PLAN}</div>) ||
							(plan.expirationDate && (
								<div>
									{lang.ACTIVE_PLAN.NOT_LIFETIME_PLAN} {plan.expirationDate}.
								</div>
							))}
					</div>
				);
			case ACTIVE_SUBSCRIPTION:
				return (
					<div>
						<div>{lang.ACTIVE_SUBSCRIPTION.MAIN}</div>
						<div>{this.renderProducts(plan.products)}</div>
						{(plan.isLifetime && <div>{lang.ACTIVE_SUBSCRIPTION.LIFETIME_PLAN}</div>) ||
							((plan.renewalDate &&
								(plan.price && (
									<div>
										Your plan will renew for {plan.price} on {plan.renewalDate}.
									</div>
								))) ||
								(plan.nextEndDate && (
									<div>
										Your plan will switch from a subscription to a one-off plan on{' '}
										{plan.renewalDate}. The new plan will end on {plan.nextEndDate}.
									</div>
								)))}
					</div>
				);
			case NO_ACTIVE_PLAN:
				return (
					<div>
						<div>{lang.NO_ACTIVE_PLAN}</div>
					</div>
				);
			case FAILED_SUBSCRIPTION:
				return (
					<div>
						<div>{lang.FAILED_SUBSCRIPTION}</div>
					</div>
				);
			case PAST_DUE_SUBSCRIPTION:
				return (
					<div>
						<div>{lang.PAST_DUE_SUBSCRIPTION}</div>
					</div>
				);
			case CANCELLED_SUBSCRIPTION:
				return (
					<div>
						<div>{lang.CANCELLED_SUBSCRIPTION}</div>
					</div>
				);
			default:
				CK.logger.warn('Invalid payment plan status', {
					plan
				});
				return <div>{lang.INVALID_PAYMENT_PLAN}</div>;
		}
	}

	render() {
		const { statusInformation, actions } = this.getPlanStatusAndActions(this.props.planInfo);

		let instructorMessage = null;

		if (CK.user.isCampInstructor()) {
			const role = _.find(CK.user.roles().$(), role =>
				_.includes([CK.const.ROLES.CAMP_ADMIN.name, CK.const.ROLES.CAMP_LEADER.name], role.name)
			);

			const camp = _.get(role, ['camps', 0]);

			if (role && camp) {
				instructorMessage = (
					<div className="campInstructorMessage">
						{lang.CAMP_MESSAGE.MAIN}{' '}
						<a href={`https://${camp.url}${CK.config.environment.campDomainBase}/`}>
							Open the {camp.url} dashboard
						</a>.
					</div>
				);
			} else {
				CK.logger.warn('isCampInstructor but no role or no camp found');
			}
		}

		return (
			<div className="CurrentPaymentPlanCard">
				{instructorMessage}
				{statusInformation}
				<div>{actions}</div>
			</div>
		);
	}
}

CurrentPlanStatusAndActions.propTypes = {
	planInfo: CurrenPlanInfoPropType,
	showPaymentForm: PropTypes.func,
	goToCancelPage: PropTypes.func,
	goToChangePlanPage: PropTypes.func
};
